import { useCallback } from 'react'

import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'

import { useAppDispatch } from 'src/Hooks/redux'
import { getUserId } from 'src/Redux/auth-process/userSlice/selectors'
import { useScheduleStartMutation } from 'src/Redux/analyticsApi'
import { CampaignPlatformTypeEnum, CampaignTypeEnum } from 'src/Types/campaigns'
import { useSpotifyCampaignStatus } from 'src/Redux/spotifyCampaignStatusApi'
import {
  campaignBudgetReceived,
  campaignScheduleStartEvent,
  campaignBudgetUpdateEvent,
} from 'src/Helpers/TagManager'
import { getScheduledTikTokCampaign } from 'src/Redux/schedule-process/tiktokCampaign/selectors'
import { ANY_LANGUAGE_ID } from 'src/Constants/constants'
import {
  getIsPriceReceived,
  setPriceReceived,
} from 'src/Redux/analytics-process'
import { getAccessToken } from 'src/Redux/auth-process/credentialsSlice/selectors'
import { sendSourceBusterEvent } from 'src/Redux/sourcebuster-process/api-actions'
import {
  getCookieByName,
  getUtmParameters,
  getValueOrZero,
} from 'src/Constants/functions'
import { getValueOrEmptyString } from 'src/Helpers/layoutHelpers'
import { useGetSubscriptionQuery } from 'src/Redux/subscription-process/subscriptionApi'
import { getAllCampaignsCount, useMetadataQuery } from 'src/Redux/metadataApi'
import { useGetMetaAdsCampaignByIdQuery } from 'src/Redux/adsCampaignApi'
import { ArtistRoutesEnum } from 'src/Router/ArtistComponentsNewUI'

import { useDuplicateEvents } from './useDuplicateEvents'
interface ScheduleEvents {
  handleScheduleStartEvent: (params: {
    campaignType: string | CampaignTypeEnum
    platformName: string
    campaignId: string | number
    genreList?: string
  }) => void

  handleBudgetEvents: (params: {
    platformName: string
    resultPrice?: number
  }) => void
}

export const useScheduleEvents = (): ScheduleEvents => {
  const dispatch = useAppDispatch()
  const [scheduleStart] = useScheduleStartMutation()
  const { data: subscription, isSuccess } = useGetSubscriptionQuery()
  const planName = subscription?.package?.name
  const param = useParams()
  const campaignId = Number(param.number)
  const userId = useSelector(getUserId)
  const { data: campaigns } = useMetadataQuery(null)

  const amountOfCampaigns = getAllCampaignsCount(campaigns)
  const { campaignDetails } = useSpotifyCampaignStatus()
  const scheduledCampaign = useSelector(getScheduledTikTokCampaign)
  const isPriceReceived = useSelector(getIsPriceReceived)
  const token = useSelector(getAccessToken)

  const isMetaSchedulePage = window.location.href.includes(
    ArtistRoutesEnum.AdsSchedule,
  )
  const { data } = useGetMetaAdsCampaignByIdQuery(Number(campaignId), {
    skip: !isMetaSchedulePage,
  })

  const { isEventCalled } = useDuplicateEvents('schedule_start')

  const handleScheduleStartEvent = useCallback(
    (params: {
      campaignType: string | CampaignTypeEnum
      platformName: string
      campaignId: string | number
      genreList?: string
    }): void => {
      if (!isSuccess) {
        return
      }

      const numericCampaignId = Number(params.campaignId) || campaignId
      const key = `${numericCampaignId}_${userId}`

      if (!isEventCalled(key)) {
        return
      }

      const utmObj = getUtmParameters(getCookieByName('soundCampaignUtm'))

      campaignScheduleStartEvent({
        platformName: params.platformName,
        campaignId: numericCampaignId,
        campaignType: params.campaignType || CampaignTypeEnum.BASIC,
        userId: String(userId),
        campaignNumber: amountOfCampaigns,
        genreList: getValueOrEmptyString(params.genreList),
        subscriptionPlan: getValueOrEmptyString(planName),
      })

      void scheduleStart({
        campaignId: numericCampaignId,
        campaignPlatform: params.platformName as CampaignPlatformTypeEnum,
      })

      if (params.campaignType === CampaignTypeEnum.RELAUNCHED) {
        void dispatch(
          sendSourceBusterEvent({
            token,
            campaignId: numericCampaignId,
            campaignPlatform: params.platformName as CampaignPlatformTypeEnum,
            eventName: 'schedule_relaunch_campaign',
            gclid: getValueOrEmptyString(utmObj.gclid),
          }),
        )
      }
    },
    [
      isSuccess,
      campaignId,
      userId,
      isEventCalled,
      amountOfCampaigns,
      planName,
      scheduleStart,
      dispatch,
      token,
    ],
  )

  const handleBudgetReceivedEvent = (params: {
    platformName: string
    resultPrice?: number
  }): void => {
    params.platformName === CampaignPlatformTypeEnum.TIKTOK &&
      campaignBudgetReceived({
        campaignType: scheduledCampaign.type || CampaignTypeEnum.BASIC,
        platformName: CampaignPlatformTypeEnum.TIKTOK,
        campaignNumber: amountOfCampaigns,
        campaignId: Number(scheduledCampaign.id) || campaignId,
        userId: String(userId),
        resultPrice: getValueOrZero(params.resultPrice),
        genreList: '',
        isAnyLanguage: false,
        subscriptionPlan: getValueOrEmptyString(planName),
      })

    params.platformName === CampaignPlatformTypeEnum.SPOTIFY &&
      campaignBudgetReceived({
        campaignType: campaignDetails?.type || CampaignTypeEnum.BASIC,
        platformName: CampaignPlatformTypeEnum.SPOTIFY,
        campaignNumber: amountOfCampaigns,
        campaignId: Number(campaignDetails?.id) || campaignId,
        userId: String(userId),
        resultPrice: params.resultPrice || 0,
        genreList: getValueOrEmptyString(
          campaignDetails?.genres?.map((genre) => genre.name).join(),
        ),
        isAnyLanguage: Boolean(
          campaignDetails?.languages
            ?.map((language) => language.id)
            .find((id) => id === ANY_LANGUAGE_ID),
        ),
        subscriptionPlan: getValueOrEmptyString(planName),
      })
  }

  const handleBudgetUpdateEvent = (params: {
    platformName: string
    resultPrice?: number
  }): void => {
    const basePayload = {
      campaignNumber: amountOfCampaigns,
      campaignId: Number(scheduledCampaign.id) || campaignId,
      userId: String(userId),
      resultPrice: params.resultPrice || 0,
      subscriptionPlan: getValueOrEmptyString(planName),
    }

    switch (params.platformName) {
      case CampaignPlatformTypeEnum.TIKTOK:
        campaignBudgetUpdateEvent({
          ...basePayload,
          campaignType: scheduledCampaign.type || CampaignTypeEnum.BASIC,
          platformName: CampaignPlatformTypeEnum.TIKTOK,
          genreList: '',
        })
        break

      case CampaignPlatformTypeEnum.META:
        campaignBudgetUpdateEvent({
          ...basePayload,
          campaignType: CampaignTypeEnum.BASIC,
          platformName: CampaignPlatformTypeEnum.META,
          genreList: data?.genres.map((genre) => genre).join(',') || '',
        })
        break

      case CampaignPlatformTypeEnum.SPOTIFY:
      default:
        campaignBudgetUpdateEvent({
          ...basePayload,
          campaignType: campaignDetails?.type || CampaignTypeEnum.BASIC,
          platformName: CampaignPlatformTypeEnum.SPOTIFY,
          genreList: getValueOrEmptyString(
            campaignDetails?.genres?.map((genre) => genre.name).join(),
          ),
        })
        break
    }
  }

  const handleBudgetEvents = (params: {
    platformName: string
    resultPrice?: number
  }): void => {
    if (!isPriceReceived) {
      handleBudgetReceivedEvent(params)
      dispatch(setPriceReceived(true))
    } else {
      handleBudgetUpdateEvent(params)
    }
  }

  return {
    handleScheduleStartEvent,
    handleBudgetEvents,
  }
}
